import { useMemo, type FC } from "react";
import type { Dayjs } from "dayjs";

import AffiliateKpisOverview, {
  type AffiliateKpisMetricId,
} from "../../AffiliateKpis/AffiliateKpisOverview";
import { Select } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useAffiliateDetailKpiCards } from "../../../../queries/affiliates/useAffiliateKpiCards";

import styles from "./AffiliateProfileKpisPanel.module.css";

export type AffiliateProfileKpisFilterOption = {
  value: string;
  label: string;
};

export type AffiliateProfileKpisPanelProps = {
  affiliateId?: string;
  // Range owned by the AffiliateProfile header. The panel uses it only
  // to scope the KPI fetch — the date picker + download button live in
  // the page header, so the panel itself renders no filter row.
  dates: [Dayjs, Dayjs];
  // Toggle for the collapsible Code filter panel. The toggle button lives
  // next to the tab strip (in `AffiliateProfile`), so state is lifted.
  showFilters: boolean;
  // Code filter — parent owns the picked value + the option list so the
  // same selection also feeds the four charts inside `AffiliateKpisOverview`.
  // Optional so filterless embeds (Edit Portal View modal) stay minimal.
  selectedCode?: string;
  onCodeChange?: (next: string | undefined) => void;
  codeOptions?: ReadonlyArray<AffiliateProfileKpisFilterOption>;
  isLoadingCodes?: boolean;
  // Forwarded to AffiliateKpisOverview — used by the Edit Portal View modal
  // to preview the affiliate's gated card set.
  visibleMetrics?: ReadonlyArray<AffiliateKpisMetricId>;
};

/**
 * Thin adapter that feeds `AffiliateKpisOverview` (originally written
 * for the Affiliates LIST page) with the per-affiliate KPI block,
 * scoped to the profile-header date range. The presentational overview
 * stays product-agnostic — this panel owns the data fetch.
 */
const AffiliateProfileKpisPanel: FC<AffiliateProfileKpisPanelProps> = ({
  affiliateId,
  dates,
  showFilters,
  selectedCode,
  onCodeChange = () => {},
  codeOptions = [],
  isLoadingCodes = false,
  visibleMetrics,
}) => {
  const dateRange = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    }),
    [dates],
  );
  const kpisQuery = useAffiliateDetailKpiCards(affiliateId, dateRange, {
    promo_code: selectedCode,
  });

  return (
    <div className={styles.container} data-testid="affiliate-profile-kpis-panel">
      <div
        className={styles.filtersPanel}
        data-testid="affiliate-profile-kpis-filters-panel"
        style={{ display: showFilters ? "grid" : "none" }}
      >
        <Select<string>
          className={styles.codeSelect}
          placeholder={AppStrings.ALL_CODES}
          value={selectedCode}
          onChange={(value) => onCodeChange(value)}
          allowClear
          showSearch
          optionFilterProp="label"
          options={codeOptions as { value: string; label: string }[]}
          loading={isLoadingCodes}
          data-testid="affiliate-profile-kpis-code-select"
        />
      </div>
      <AffiliateKpisOverview
        kpis={kpisQuery.data}
        isLoading={kpisQuery.isFetching}
        scope="affiliate"
        affiliateId={affiliateId}
        promoCode={selectedCode}
        codeOptions={codeOptions}
        isLoadingCodes={isLoadingCodes}
        visibleMetrics={visibleMetrics}
      />
    </div>
  );
};

export default AffiliateProfileKpisPanel;
